import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import Select from "react-select";
import { getAuthCookie } from "../utils/authCookies";

interface ModalInputProps {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  placeholder?: string;
  type?: "text" | "number";
  min?: number;
  required?: boolean;
  error?: string;
  showError?: boolean;
}

const ModalInput: React.FC<ModalInputProps> = ({
  label,
  name,
  value,
  onChange,
  onBlur,
  placeholder,
  type = "text",
  min,
  required,
  error,
  showError,
}) => {
  const displayError = showError && error;
  return (
    <div>
      <label className="form-label fw-medium mb-2">
        {label} {required && <span className="text-danger">*</span>}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        min={min}
        className={`form-control custom-modal-input ${displayError ? "is-invalid" : ""}`}
      />
      {displayError && <div className="invalid-feedback d-block">{error}</div>}
    </div>
  );
};

interface CustomItemModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void | Promise<void>;
}

type UpsPackage = {
  id: string;
  text?: string;
  name: string;
  length: number;
  width: number;
  height: number;
  weight: number;
  unit?: string;
};

type UpsPackageOption = {
  value: string;
  label: string;
  data: UpsPackage;
};

const initialValues = {
  item_name: "",
  custom_info: "",
  price: "",
  quantity: "",
  taxable: false,
  length: "",
  height: "",
  width: "",
  weight: "",
};

function validateDimension(value: string, fieldName: string): string | undefined {
  if (value === "") return undefined;
  const num = Number(value);
  if (isNaN(num)) return undefined;
  if (num < 0) return `${fieldName} cannot be negative`;
  return undefined;
}

function validate(values: typeof initialValues) {
  const errors: Partial<Record<keyof typeof initialValues, string>> = {};
  if (!values.item_name?.trim()) {
    errors.item_name = "Item name is required";
  }
  const qty = Number(values.quantity);
  if (values.quantity === "" || isNaN(qty)) {
    errors.quantity = "Quantity is required";
  } else if (qty < 1) {
    errors.quantity = "Quantity must be at least 1";
  }
  const price = Number(values.price);
  if (values.price === "" || isNaN(price)) {
    errors.price = "Price is required";
  } else if (price < 0) {
    errors.price = "Price cannot be negative";
  } else if (price < 1) {
    errors.price = "Price must be at least 1";
  }
  const lengthErr = validateDimension(values.length, "Length");
  if (lengthErr) errors.length = lengthErr;
  const heightErr = validateDimension(values.height, "Height");
  if (heightErr) errors.height = heightErr;
  const widthErr = validateDimension(values.width, "Width");
  if (widthErr) errors.width = widthErr;
  const weightErr = validateDimension(values.weight, "Weight");
  if (weightErr) errors.weight = weightErr;
  return errors;
}

const CustomItemModal: React.FC<CustomItemModalProps> = ({
  show,
  onClose,
  onSubmit,
}) => {
  const token = getAuthCookie("sessionToken");
  const [upsPackages, setUpsPackages] = useState<UpsPackageOption[]>([]);
  const [selectedUpsPackage, setSelectedUpsPackage] = useState<UpsPackageOption | null>(null);
  const [upsPackageSearch, setUpsPackageSearch] = useState("");
  const [isLoadingUpsPackages, setIsLoadingUpsPackages] = useState(false);

  const formik = useFormik({
    initialValues,
    validate,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (values) => {
      await onSubmit(values);
      onClose();
    },
  });

  useEffect(() => {
    if (!show) {
      formik.resetForm({ values: initialValues });
      setSelectedUpsPackage(null);
      setUpsPackageSearch("");
    }
  }, [show]);

  useEffect(() => {
    if (!show || !token) return;

    const controller = new AbortController();
    const timeoutId = window.setTimeout(async () => {
      try {
        setIsLoadingUpsPackages(true);
        const params = new URLSearchParams({
          q: upsPackageSearch,
          page: "1",
          limit: "10",
        });

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/shipping/ups/packages/select2?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            signal: controller.signal,
          }
        );

        if (!response.ok) throw new Error("Failed to fetch UPS packages");

        const data = await response.json();
        const results: UpsPackage[] = data?.results || [];
        setUpsPackages(
          results.map((pkg) => ({
            value: pkg.id,
            label: `${pkg.name || pkg.text || "UPS Package"} (${pkg.length} * ${pkg.width} * ${pkg.height} * ${pkg.weight})`,
            // label: `${pkg.name || pkg.text || "UPS Package"} (${pkg.length} * ${pkg.width} * ${pkg.height} * ${pkg.weight}${pkg.unit ? ` ${pkg.unit}` : ""})`,
            data: pkg,
          }))
        );
      } catch (error) {
        if ((error as Error).name !== "AbortError") {
          console.error("Failed to fetch UPS packages:", error);
          setUpsPackages([]);
        }
      } finally {
        setIsLoadingUpsPackages(false);
      }
    }, 300);

    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [show, token, upsPackageSearch]);

  const handleUpsPackageChange = (option: UpsPackageOption | null) => {
    setSelectedUpsPackage(option);

    if (!option) return;

    formik.setFieldValue("length", String(option.data.length ?? ""));
    formik.setFieldValue("width", String(option.data.width ?? ""));
    formik.setFieldValue("height", String(option.data.height ?? ""));
    formik.setFieldValue("weight", String(option.data.weight ?? ""));
  };

  if (!show) return null;

  return (
    <>
    <div className="custom-item-modal-overlay">
      <div className="custom-item-modal-wrapper" onClick={(e) => e.stopPropagation()}>

        {/* HEADER - fixed */}
        <div className="custom-item-modal-header">
          <h5>Add Custom Product</h5>
          <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
        </div>

        {/* BODY - scrollable */}
        <div className="custom-item-modal-body">
        <form className="custom-item-form" onSubmit={formik.handleSubmit} noValidate>
        <div className="mb-4">
          <ModalInput
            label="Item Name"
            name="item_name"
            value={formik.values.item_name}
            placeholder="Enter item name"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            required
            error={formik.errors.item_name}
            showError={(formik.touched.item_name || formik.submitCount > 0) && !!formik.errors.item_name}
          />
        </div>

        <div className="mb-4">
          <label className="form-label fw-medium mb-2">
            Custom Information
          </label>
          <textarea
            className="form-control custom-modal-input"
            rows={3}
            placeholder="Enter custom information or product description"
            name="custom_info"
            value={formik.values.custom_info}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </div>

        <div className="row mb-4">
          <div className="col-md-6">
            <ModalInput
              label="Price"
              name="price"
              type="number"
              value={formik.values.price}
              placeholder="0.00"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              required
              error={formik.errors.price}
              showError={(formik.touched.price || formik.submitCount > 0) && !!formik.errors.price}
            />
          </div>

          <div className="col-md-6">
            <ModalInput
              label="Quantity"
              name="quantity"
              type="number"
              value={formik.values.quantity}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              required
              error={formik.errors.quantity}
              showError={(formik.touched.quantity || formik.submitCount > 0) && !!formik.errors.quantity}
            />
          </div>
        </div>

        <div className="mb-4">
          <div className="custom-taxable-box">
            <input
              type="checkbox"
              name="taxable"
              checked={formik.values.taxable}
              onChange={formik.handleChange}
            />
            <span>Item Taxable</span>
          </div>
        </div>

        <h6 className="fw-semibold mb-3">Dimensions</h6>

        <div className="mb-4 col-md-6">
          <label className="form-label fw-medium mb-2">Select Package Type</label>
          <Select
            classNamePrefix="custom-ups-package-select"
            value={selectedUpsPackage}
            options={upsPackages}
            isClearable
            isLoading={isLoadingUpsPackages}
            placeholder="Select Box"
            noOptionsMessage={() => "No packages found"}
            onInputChange={(value, actionMeta) => {
              if (actionMeta.action === "input-change") {
                setUpsPackageSearch(value);
              }
            }}
            onChange={handleUpsPackageChange}
          />
        </div>

        <div className="row mb-3">
          <div className="col-md-4">
            <ModalInput
              label="Length (in)"
              name="length"
              type="number"
              value={formik.values.length}
              placeholder="0"
              min={0}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.errors.length}
              showError={(formik.touched.length || formik.submitCount > 0) && !!formik.errors.length}
            />
          </div>

          <div className="col-md-4">
            <ModalInput
              label="Height (in)"
              name="height"
              type="number"
              value={formik.values.height}
              placeholder="0"
              min={0}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.errors.height}
              showError={(formik.touched.height || formik.submitCount > 0) && !!formik.errors.height}
            />
          </div>

          <div className="col-md-4">
            <ModalInput
              label="Width (in)"
              name="width"
              type="number"
              value={formik.values.width}
              placeholder="0"
              min={0}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.errors.width}
              showError={(formik.touched.width || formik.submitCount > 0) && !!formik.errors.width}
            />
          </div>
        </div>

        <ModalInput
          label="Weight (lbs)"
          name="weight"
          type="number"
          value={formik.values.weight}
          placeholder="0"
          min={0}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.weight}
          showError={(formik.touched.weight || formik.submitCount > 0) && !!formik.errors.weight}
        />
        </form>
        </div>

        {/* FOOTER - fixed */}
        <div className="custom-item-modal-footer justify-content-between">
          <button type="button" className="btn back-btn surface-btn fw-bold fs-16" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary save-btn surface-btn fw-bold fs-16"
            disabled={formik.isSubmitting}
            onClick={() => formik.handleSubmit()}
          >
            {formik.isSubmitting ? "Adding..." : "Add Product"}
          </button>
        </div>
      </div>
    </div>
    <style>{`
/* Overlay - same pattern as ShippingPresets PresetModal */
.custom-item-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

/* Wrapper - flex column, constrained height */
.custom-item-modal-wrapper {
  background: #fff;
  width: 100%;
  max-width: 720px;
  max-height: 90vh;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.12);
}

/* Mobile: full screen */
@media (max-width: 767px) {
  .custom-item-modal-wrapper {
    max-height: 100vh;
    border-radius: 0;
  }
}

/* Header - fixed */
.custom-item-modal-header {
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
}

.custom-item-modal-header h5 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

/* Body - scrollable with visible scrollbar */
.custom-item-modal-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 18px 20px 20px;
  -webkit-overflow-scrolling: touch;
}

.custom-item-modal-body::-webkit-scrollbar {
  width: 8px;
}

.custom-item-modal-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.custom-item-modal-body::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.custom-item-modal-body::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Footer - fixed */
.custom-item-modal-footer {
  padding: 14px 20px;
  border-top: 1px solid #eee;
  display: flex;
  gap: 12px;
  background: #fff;
  flex-shrink: 0;
}

.custom-modal-input {
  border-radius: 10px !important;
  border: 1px solid #d1d5db !important;
  background-color: #f9fafb !important;
  height: 42px;
}

.custom-modal-input:focus {
  border-color: #3b82f6 !important;
  background-color: #ffffff !important;
  box-shadow: none !important;
}

textarea.custom-modal-input {
  height: auto !important;
  min-height: 90px;
}

.custom-taxable-box {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  background-color: #f9fafb;
}

.custom-submit-btn {
  border-radius: 10px;
  height: 44px;
  font-weight: 500;
}

.custom-cancel-btn {
  border-radius: 10px;
  height: 44px;
  background-color: #f3f4f6;
}
    `}</style>
    </>
  );
};

export default CustomItemModal;